// ENERGY v2 — the frame the rebuilt pages give Energy.tsx's lines (UI rebuild batch 4, adopted by the energy lane 27 Oct 2026).
// Every rule, word and line is screens/Energy.tsx's, imported — ONE copy (tests/energy-v2 fails if a copy comes back). Only the
// frame is here:
//   V2_SLABS    the rebuilt console's bordered box for a line that must be seen on its own (the kit's Notice look)
//   DeskLines   Energy.tsx's ReadLines — is there a picture, and is it current? — in that box, with the kit's tap target
//   Explorer    the chain explorer as an icon
//   AlarmLine   one of the desk's alarms as ONE line in that box, the way the list says it, the whole sentence behind Details
//   ONE FACT PER CELL (his words, 1 Nov 2026: "Amount, chain, date and time everything in 1st column- doesn't it look wrong",
//   "check everything where it applies"):
//   EnergyNow   a wallet's energy as a figure and its unit, or "—" — never a state in its place (the wallet's tag says that)
//   EnergyNeed  what it needs, the same way (walletNeed)
//   LifeTag     paused or retired as a tag of its own beside the name, never words in the name
//   ToneWord    a state as a word in its tone, where a tag would not fit (the column beside a form); StateWord, a wallet's
//   besideForm / underForm   the context column where it fits: beside the form from xl, under it from lg to xl
//   EnergyFigure an amount of energy: the figure, its unit muted; TrxFigure the same for TRX
//   FactList    facts as labelled lines, compact — a phone card's (what the laptop shows as columns), a price's
import { Fragment, type ReactNode } from 'react';
import { ExternalLink } from 'lucide-react';
import type { EnergyAlarm, EnergyStatus, EnergyWallet } from '../../../lib/api';
import { alarmAbout, alarmLead } from '../../../lib/energyOrder';
import { StateLine, TAP } from '../kit';
import { Pill, type PillTone } from '../../Pill';
import {
  ReadLines, explorerAddress, type Desk, type Slabs, namedWallets, lowOnEnergy, units, walletLifecycle, walletNeed, walletSeat,
} from '../../../screens/Energy';

/** The rebuilt console's box for a line that must be seen on its own: red fails or refuses, amber needs him, grey is plain. */
export const V2_SLABS: Slabs = {
  bad: 'rounded-lg border border-neg/25 bg-neg-bg px-4 py-3',
  warn: 'rounded-lg border border-warn/25 bg-warn-bg px-4 py-3',
  neutral: 'rounded-lg border border-stroke bg-panel-2 px-4 py-3',
};

/** The desk's own "Try again", as a tap target of the kit. */
export const tryAgainOf = (refresh: () => void): ReactNode => <button type="button" className={TAP} onClick={refresh}>Try again</button>;

/** Before anything else: is there a picture, and is it current? Energy.tsx's lines, each in the rebuilt console's box. `rows`: the
 *  page draws its own grey rows while the first read is on its way (the list) — otherwise the line says it is reading. */
export function DeskLines({ desk, rows = false }: { desk: Desk; rows?: boolean }) {
  return <ReadLines desk={desk} tryAgain={tryAgainOf(desk.refresh)} slabs={V2_SLABS} reading={rows ? null : undefined} />;
}

export function Explorer({ network, address }: { network: EnergyStatus['network']; address: string }) {
  return (
    <a href={explorerAddress(network, address)} target="_blank" rel="noreferrer" className="tap-44 inline-flex text-info hover:text-tg-text lg:-m-2 lg:p-2" title="Open in the chain explorer" aria-label="Open in the chain explorer">
      <ExternalLink size={12} />
    </a>
  );
}

/** One of the desk's alarms as ONE line in its box, the way the list says it (alarmLead — the consequence never dropped), the whole
 *  sentence behind Details, and its way out in the box when the sheet gives one (`action`: the rental it names). */
export function AlarmLine({ a, d, action }: { a: EnergyAlarm; d: EnergyStatus; action?: ReactNode }) {
  const { lead, folded } = alarmLead(a.key, a.text, alarmAbout(a, namedWallets(d)));
  const tone = a.level === 'critical' ? 'bad' : 'warn';
  return <StateLine tone={tone} className={V2_SLABS[tone]} more={folded ? <p>{a.text}</p> : undefined} action={action}>{lead}</StateLine>;
}

/** Is there a current reading of the wallet's energy? Not when it is retired (the desk no longer reads it), its chain read failed,
 *  it is not on-chain, or it has not been read yet — its tag says which. */
export const hasReading = (w: EnergyWallet): boolean => w.status !== 'retired' && !w.observeError && w.activated !== false && w.available !== null;
const Unknown = () => <span className="text-tg-hint">—</span>;
/** An amount of energy: the figure, then its unit muted, on one line. */
export const EnergyFigure = ({ n, tone = '' }: { n: string; tone?: string }) => (
  <span className="whitespace-nowrap"><span className={`font-mono tabular ${tone}`}>{units(n)}</span> <span className="text-meta text-tg-hint">energy</span></span>
);

/** TRX: the figure (as trx, rateTrx or rentalTrx says it), then its unit muted, on one line — or "—" alone when there is none. */
export const TrxFigure = ({ value, big = false, tone = '', title }: { value: string; big?: boolean; tone?: string; title?: string }) => (value === '—'
  ? <Unknown />
  : <span className="whitespace-nowrap" title={title}><span className={`font-mono tabular ${big ? 'text-title font-semibold' : ''} ${tone}`}>{value}</span> <span className="text-meta text-tg-hint">TRX</span></span>);

/** A wallet's energy as a figure and its unit — amber when it holds less than one transfer while at work — or "—" with no current
 *  reading. Never a state in its place: "being activated", "read failed" are the wallet's tag. */
export function EnergyNow({ w }: { w: EnergyWallet }) {
  return hasReading(w) ? <EnergyFigure n={w.available as string} tone={lowOnEnergy(w) ? 'text-warn' : ''} /> : <Unknown />;
}

/** What a wallet needs (walletNeed) as a figure and its unit: amber when it is short, a muted 0 when it has enough, "—" when it
 *  cannot be said. */
export function EnergyNeed({ w }: { w: EnergyWallet }) {
  const short = walletNeed(w);
  return short === null ? <Unknown /> : <EnergyFigure n={String(short)} tone={short > 0n ? 'text-warn' : 'text-tg-hint'} />;
}

/** Paused or retired, as a tag of its own beside the wallet's name — shown only when its state tag does not already say it. */
export function LifeTag({ w, d }: { w: EnergyWallet; d: EnergyStatus }) {
  const life = walletLifecycle(w);
  return life !== null && life.word !== walletSeat(d, w).word ? <Pill tone={life.tone}>{life.word}</Pill> : null;
}

/** A state as a word in its tone, a fact beside the form's figures (StateWord: a wallet's, walletSeat). */
const WORD_TONE: Record<PillTone, string> = { neutral: 'text-tg-hint', good: 'text-pos', warn: 'text-warn', bad: 'text-neg', info: 'text-info', gold: 'text-brand-gold' };
export const ToneWord = ({ tone, word }: { tone: PillTone; word: string }) => <span className={WORD_TONE[tone]}>{word}</span>;
export function StateWord({ w, d }: { w: EnergyWallet; d: EnergyStatus }) {
  const seat = walletSeat(d, w);
  return <ToneWord tone={seat.tone} word={seat.word} />;
}

/**
 * THE CONTEXT COLUMN WHERE IT FITS. The form frame (components/v2/move/FormFrame, UI rebuild's) puts it beside a 600 px form from lg:
 * at 1024 that leaves it about 124 px — a label, and a figure one character a line (Astra, 1 Nov 2026, on the renders). Beside the
 * form from xl only; from lg to xl the same column under the form, at its width; on the phone, as the frame has it (none).
 * `data-context` is what the bench's check reads (bench/views-b4-energy.mjs: no figure there breaks across lines).
 */
export const besideForm = (node: ReactNode): ReactNode => (node ? <div className="hidden xl:block" data-context="">{node}</div> : node);
export const underForm = (node: ReactNode): ReactNode => (node ? <div className="mb-4 hidden lg:block xl:hidden" data-context="">{node}</div> : node);

/** Facts as labelled lines, compact — the label muted at the left, its value beside it: a phone card's (what the laptop shows as a
 *  column, the card shows as a labelled line, never run together in a sentence; `left`: the card's title shrinks to what it holds,
 *  so its values start after the labels), a price's (at the box's right edge). */
export function FactList({ facts, left = false }: { facts: ReadonlyArray<readonly [string, ReactNode]>; left?: boolean }) {
  return (
    <dl className="mt-2 grid grid-cols-[auto_minmax(0,1fr)] items-baseline gap-x-4 gap-y-1 text-body font-normal">
      {facts.map(([label, value]) => (
        <Fragment key={label}>
          <dt className="text-meta text-tg-hint">{label}</dt>
          <dd className={`min-w-0 ${left ? '' : 'text-right'}`}>{value}</dd>
        </Fragment>
      ))}
    </dl>
  );
}
